import {
    useBlockProps,
    InspectorControls,
    BlockControls,
    BlockAlignmentToolbar,
} from "@wordpress/block-editor";
import {
    PanelBody,
    SelectControl,
    TextControl,
    TextareaControl,
    Button,
    ButtonGroup,
    ToggleControl,
    Disabled,
    Spinner,
    Notice,
    __experimentalNumberControl as NumberControl,
    ToolbarGroup,
    ToolbarDropdownMenu,
} from "@wordpress/components";
import DebouncedServerSideRender from "../../../shared/js/DebouncedServerSideRender";
import HydratedSourceControl from "../../../shared/js/HydratedSourceControl";
import useLinkedProfile from "../../../shared/js/useLinkedProfile";
import { __, sprintf } from "@wordpress/i18n";

const HEADING_TAG_OPTIONS = ["h1", "h2", "h3", "h4", "div"];

const VARIANT_OPTIONS = [
    { label: __("List", "content-egg"),      value: "pricing-list" },
    { label: __("Cards", "content-egg"),     value: "pricing-cards" },
    { label: __("Highlight", "content-egg"), value: "pricing-highlight" },
];

const DATA_SOURCE_OPTIONS = [
    { value: "auto",   label: __("Auto-fill from linked profile", "content-egg") },
    { value: "manual", label: __("Manual override (custom items)", "content-egg") },
];

const addItem = (items, setAttributes) => {
    setAttributes({
        items: [
            ...items,
            { name: "", description: "", price: "", priceFrom: "", priceTo: "",
              billingPeriod: "", featuresSummary: [], isFeatured: false, ctaLabel: "", ctaUrl: "" },
        ],
    });
};

const updateItem = (items, index, key, value, setAttributes) => {
    const updated = items.map((item, i) => i === index ? { ...item, [key]: value } : item);
    setAttributes({ items: updated });
};

const removeItem = (items, index, setAttributes) => {
    setAttributes({ items: items.filter((_, i) => i !== index) });
};

const addPromotion = (promotions, setAttributes) => {
    setAttributes({ promotions: [...promotions, { title: "", description: "" }] });
};

const updatePromotion = (promotions, index, key, value, setAttributes) => {
    const updated = promotions.map((p, i) => i === index ? { ...p, [key]: value } : p);
    setAttributes({ promotions: updated });
};

const removePromotion = (promotions, index, setAttributes) => {
    setAttributes({ promotions: promotions.filter((_, i) => i !== index) });
};

export default function Edit({ attributes, setAttributes }) {
    const blockProps = useBlockProps({
        className: attributes.align ? `align${attributes.align}` : "",
    });

    const {
        variant,
        heading_tag,
        color_scheme,
        data_source,
        items_limit,
        title,
        intro_text,
        general_price_note,
        cta_label,
        cta_url,
        cta_url_source,
        featured_label,
        items,
        promotions,
    } = attributes;

    const { loading: profileLoading, summary: linkedProfile } = useLinkedProfile();

    // Effective mode: auto only if a profile is actually linked AND user picked auto.
    const useSource = !!linkedProfile && data_source !== "manual";

    // URL options for the cta_url dropdown.
    const urlOptions = (linkedProfile?.available_url_keys ?? []).map((entry) => ({
        value: entry.key,
        label: entry.label,
    }));

    // In auto mode the block will hydrate at render time even with empty literal items;
    // in manual mode we show a placeholder until the user adds items.
    const hasManualData = items.length > 0;
    const showPreview = useSource || hasManualData;

    return (
        <>
            <BlockControls>
                <ToolbarGroup>
                    <ToolbarDropdownMenu
                        icon="screenoptions"
                        label={__("Variant", "content-egg")}
                        text={VARIANT_OPTIONS.find((o) => o.value === variant)?.label}
                        controls={VARIANT_OPTIONS.map((option) => ({
                            title: option.label,
                            isActive: variant === option.value,
                            onClick: () => setAttributes({ variant: option.value }),
                        }))}
                    />
                </ToolbarGroup>
                <BlockAlignmentToolbar
                    value={attributes.align}
                    onChange={(val) => setAttributes({ align: val })}
                    controls={["wide", "full"]}
                />
            </BlockControls>

            <InspectorControls>
                <PanelBody title={__("Layout", "content-egg")} initialOpen={true}>
                    <SelectControl
                        label={__("Variant", "content-egg")}
                        value={variant}
                        options={VARIANT_OPTIONS}
                        onChange={(val) => setAttributes({ variant: val })}
                        __nextHasNoMarginBottom
                    />
                    <SelectControl
                        label={__("Color Scheme", "content-egg")}
                        value={color_scheme}
                        options={[
                            { label: __("Auto (site default)", "content-egg"), value: "auto" },
                            { label: __("Light", "content-egg"), value: "light" },
                            { label: __("Dark", "content-egg"), value: "dark" },
                        ]}
                        onChange={(val) => setAttributes({ color_scheme: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                {profileLoading && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={false}>
                        <Spinner />
                    </PanelBody>
                )}

                {!profileLoading && linkedProfile && (
                    <PanelBody title={__("Data source", "content-egg")} initialOpen={true}>
                        <p style={{ fontSize: "12px", color: "#555", margin: "0 0 8px" }}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Linked profile: %s", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </p>
                        <SelectControl
                            label={__("Items, promotions & CTA source", "content-egg")}
                            help={__(
                                "Auto-fill pulls items, promotions and the CTA URL from the linked profile at render time. Switch to manual to enter custom values for this block only.",
                                "content-egg"
                            )}
                            value={data_source || "auto"}
                            options={DATA_SOURCE_OPTIONS}
                            onChange={(val) => setAttributes({ data_source: val })}
                            __nextHasNoMarginBottom
                        />
                    </PanelBody>
                )}

                <PanelBody title={__("Content", "content-egg")} initialOpen={true}>
                    <TextControl
                        label={__("Title", "content-egg")}
                        value={title}
                        onChange={(val) => setAttributes({ title: val })}
                        __nextHasNoMarginBottom
                    />
                    <div style={{ marginBottom: "8px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 500, marginBottom: "6px" }}>
                            {__("Heading Tag", "content-egg")}
                        </div>
                        <ButtonGroup>
                            {HEADING_TAG_OPTIONS.map((tag) => (
                                <Button
                                    key={tag}
                                    variant={heading_tag === tag ? "primary" : "secondary"}
                                    size="small"
                                    onClick={() => setAttributes({ heading_tag: tag })}
                                >
                                    {tag.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <TextareaControl
                        label={__("Intro text", "content-egg")}
                        value={intro_text}
                        onChange={(val) => setAttributes({ intro_text: val })}
                        rows={2}
                        __nextHasNoMarginBottom
                    />
                    <TextControl
                        label={__("Price note / footnote", "content-egg")}
                        value={general_price_note}
                        onChange={(val) => setAttributes({ general_price_note: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Items", "content-egg")} initialOpen={true}>
                    {useSource ? (
                        <>
                            <Notice status="info" isDismissible={false}>
                                {sprintf(
                                    /* translators: %s: linked profile name */
                                    __("Items will be hydrated from %s on save.", "content-egg"),
                                    linkedProfile.profile_name
                                )}
                            </Notice>
                            <NumberControl
                                label={__("Max items to display", "content-egg")}
                                help={__("Limits how many items are shown from the profile.", "content-egg")}
                                value={items_limit}
                                min={1}
                                max={12}
                                onChange={(val) => setAttributes({ items_limit: parseInt(val, 10) || 6 })}
                                __nextHasNoMarginBottom
                            />
                        </>
                    ) : (
                        <>
                            {items.map((item, i) => (
                                <div key={i} style={{ borderBottom: "1px solid #ddd", paddingBottom: "12px", marginBottom: "12px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                        <strong style={{ fontSize: "12px" }}>{item.name || __("Item", "content-egg") + " " + (i + 1)}</strong>
                                        <Button isDestructive size="small" icon="no-alt" onClick={() => removeItem(items, i, setAttributes)} label={__("Remove", "content-egg")} />
                                    </div>
                                    <TextControl label={__("Name", "content-egg")} value={item.name} onChange={(v) => updateItem(items, i, "name", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl label={__("Description", "content-egg")} value={item.description || ""} onChange={(v) => updateItem(items, i, "description", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl label={__("Price", "content-egg")} help={__("Any string: '$49', '$3,500 – $6,000', 'From $800', 'Custom'", "content-egg")} value={item.price || ""} onChange={(v) => updateItem(items, i, "price", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl label={__("Billing period", "content-egg")} value={item.billingPeriod || ""} onChange={(v) => updateItem(items, i, "billingPeriod", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextareaControl
                                        label={__("Features (one per line)", "content-egg")}
                                        value={(item.featuresSummary || []).join("\n")}
                                        onChange={(v) => updateItem(items, i, "featuresSummary", v.split("\n"), setAttributes)}
                                        onKeyDown={(e) => e.key === "Enter" && e.stopPropagation()}
                                        rows={3}
                                        __nextHasNoMarginBottom
                                    />
                                    <TextControl label={__("CTA label", "content-egg")} value={item.ctaLabel || ""} onChange={(v) => updateItem(items, i, "ctaLabel", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl label={__("CTA URL", "content-egg")} value={item.ctaUrl || ""} onChange={(v) => updateItem(items, i, "ctaUrl", v, setAttributes)} __nextHasNoMarginBottom />
                                    <ToggleControl
                                        label={__("Featured", "content-egg")}
                                        checked={!!item.isFeatured}
                                        onChange={(val) => updateItem(items, i, "isFeatured", val, setAttributes)}
                                        __nextHasNoMarginBottom
                                    />
                                </div>
                            ))}
                            <Button variant="secondary" size="small" onClick={() => addItem(items, setAttributes)}>
                                {__("Add item", "content-egg")}
                            </Button>
                        </>
                    )}
                </PanelBody>

                <PanelBody title={__("Promotions", "content-egg")} initialOpen={false}>
                    {useSource ? (
                        <Notice status="info" isDismissible={false}>
                            {sprintf(
                                /* translators: %s: linked profile name */
                                __("Promotions will be hydrated from %s on save.", "content-egg"),
                                linkedProfile.profile_name
                            )}
                        </Notice>
                    ) : (
                        <>
                            {promotions.map((p, i) => (
                                <div key={i} style={{ borderBottom: "1px solid #ddd", paddingBottom: "10px", marginBottom: "10px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                        <strong style={{ fontSize: "12px" }}>{p.title || __("Promotion", "content-egg") + " " + (i + 1)}</strong>
                                        <Button isDestructive size="small" icon="no-alt" onClick={() => removePromotion(promotions, i, setAttributes)} label={__("Remove", "content-egg")} />
                                    </div>
                                    <TextControl label={__("Label", "content-egg")} value={p.title} onChange={(v) => updatePromotion(promotions, i, "title", v, setAttributes)} __nextHasNoMarginBottom />
                                    <TextControl label={__("Description", "content-egg")} value={p.description || ""} onChange={(v) => updatePromotion(promotions, i, "description", v, setAttributes)} __nextHasNoMarginBottom />
                                </div>
                            ))}
                            <Button variant="secondary" size="small" onClick={() => addPromotion(promotions, setAttributes)}>
                                {__("Add promotion", "content-egg")}
                            </Button>
                        </>
                    )}
                </PanelBody>

                <PanelBody title={__("Labels", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Featured item label", "content-egg")}
                        value={featured_label}
                        placeholder={__("Most popular", "content-egg")}
                        onChange={(val) => setAttributes({ featured_label: val })}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>

                <PanelBody title={__("Block CTA", "content-egg")} initialOpen={false}>
                    <TextControl
                        label={__("Label", "content-egg")}
                        value={cta_label}
                        onChange={(val) => setAttributes({ cta_label: val })}
                        __nextHasNoMarginBottom
                    />
                    <HydratedSourceControl
                        label={__("URL", "content-egg")}
                        useSource={useSource}
                        sourceValue={cta_url_source}
                        literalValue={cta_url}
                        options={urlOptions}
                        onSourceChange={(val) => setAttributes({ cta_url_source: val })}
                        onLiteralChange={(val) => setAttributes({ cta_url: val })}
                        noOptionsNotice={__("The linked profile has no URLs set.", "content-egg")}
                    />
                </PanelBody>
            </InspectorControls>

            <div {...blockProps}>
                {showPreview ? (
                    <Disabled>
                        <DebouncedServerSideRender
                            block="eggb/pricing"
                            attributes={attributes}
                        />
                    </Disabled>
                ) : (
                    <p style={{ color: "#999", fontStyle: "italic", padding: "16px", margin: 0, fontSize: "13px" }}>
                        {__("Add pricing items in the sidebar.", "content-egg")}
                    </p>
                )}
            </div>
        </>
    );
}
